import React from 'react';
import classNames from 'classnames';

import css from './IconArrowHead.module.css';

const DIRECTION_RIGHT = 'right';
const DIRECTION_LEFT = 'left';
const DIRECTION_DOWN = 'down';
const DIRECTION_UP = 'up';
const SIZE_BIG = 'big';
const SIZE_SMALL = 'small';
const SIZE_TINY = 'tiny';

/**
 * Icon with arrow head pointing to given direction and with given size.
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className add more style rules in addition to components own css.root
 * @param {string?} props.rootClassName overwrite components own css.root
 * @param {('right' | 'left' | 'down' | 'up')} props.direction
 * @param {('small' | 'big' | 'tiny')} props.size
 * @returns {JSX.Element} arrow head icon
 */
const IconArrowHead = props => {
  const { className, rootClassName, direction, size = SIZE_SMALL } = props;
  const classes = classNames(
    rootClassName ? rootClassName : size === SIZE_TINY ? css.rootTinyArrow : css.root,
    className
  );

  const isRight = direction === DIRECTION_RIGHT;
  const isLeft = direction === DIRECTION_LEFT;
  const isDown = direction === DIRECTION_DOWN;
  const isUp = direction === DIRECTION_UP;
  const isBig = size === SIZE_BIG;
  const isSmall = size === SIZE_SMALL;
  const isTiny = size === SIZE_TINY;

  if (isRight && isSmall) {
    return (
      <svg
        className={classes}
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M19 12.0001L5 12.0001M19 12.0001L13 18.0001M19 12.0001L13 6.00006"
          stroke="#0A0203"
          strokeWidth="1.02095"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    );
  } else if (isLeft && isSmall) {
    return (
      <svg
        className={classes}
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M5 12.0001L19 12.0001M5 12.0001L11 18.0001M5 12.0001L11 6.00006"
          stroke="#0A0203"
          strokeWidth="1.02095"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    );
  } else if (isDown && isSmall) {
    return (
      <svg
        className={classes}
        width="13"
        height="9"
        viewBox="0 0 13 9"
        xmlns="http://www.w3.org/2000/svg"
        role="none"
      >
        <path
          d="M6.03 6.805c.26.26.68.26.94 0l5.335-5.333a.668.668 0 0 0-.943-.943L6.5 5.39 1.638.53a.666.666 0 1 0-.943.942L6.03 6.805z"
          fillRule="evenodd"
        />
      </svg>
    );
  } else if (isUp && isSmall) {
    return (
      <svg
        className={classes}
        width="13"
        height="9"
        viewBox="0 0 13 9"
        xmlns="http://www.w3.org/2000/svg"
        role="none"
      >
        <path
          d="M6.97.195a.664.664 0 0 0-.94 0L.695 5.528a.668.668 0 0 0 .943.943L6.5 1.61l4.862 4.86a.666.666 0 1 0 .943-.942L6.97.195z"
          fillRule="evenodd"
        />
      </svg>
    );
  } else if (isRight && isBig) {
    return (
      <svg
        className={classes}
        width="15"
        height="15"
        viewBox="0 0 15 15"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M14.5 7.5L0.500001 7.5M14.5 7.5L8.5 14.5M14.5 7.5L8.5 0.5"
          stroke="#0A0203"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    );
  } else if (isLeft && isBig) {
    return (
      <svg
        className={classes}
        width="15"
        height="15"
        viewBox="0 0 15 15"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M0.5 7.5L14.5 7.5M0.5 7.5L6.5 0.499999M0.5 7.5L6.5 14.5"
          stroke="#B6B6B6"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    );
  } else if (isDown && isBig) {
    return (
      <svg
        className={classes}
        width="15"
        height="11"
        viewBox="0 0 15 11"
        xmlns="http://www.w3.org/2000/svg"
        role="none"
      >
        <path
          d="M.5 1.1C.5.93.565.76.692.642a.508.508 0 0 1 .764.07L7 7.972 12.545.71a.506.506 0 0 1 .763-.07c.23.214.257.592.064.846l-5.958 7.8A.524.524 0 0 1 7 9.498a.522.522 0 0 1-.414-.212l-5.958-7.8A.638.638 0 0 1 .5 1.098"
          fillRule="evenodd"
        />
      </svg>
    );
  } else if (isUp && isBig) {
    return (
      <svg
        className={classes}
        width="15"
        height="11"
        viewBox="0 0 15 11"
        xmlns="http://www.w3.org/2000/svg"
        role="none"
      >
        <path
          d="M13.5 8.9c0 .17-.065.34-.192.458a.508.508 0 0 1-.764-.07L7 2.028 1.455 9.29a.506.506 0 0 1-.763.07.644.644 0 0 1-.064-.846l5.958-7.8A.524.524 0 0 1 7 .502c.16 0 .31.077.414.212l5.958 7.8c.086.113.128.25.128.388"
          fillRule="evenodd"
        />
      </svg>
    );
  } else if (isRight && isTiny) {
    return (
      <svg
        className={classes}
        width="5"
        height="8"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        role="none"
      >
        <path
          fillRule="evenodd"
          d="M3.903 3.763c.13.131.13.342 0 .473L1.238 6.902a.333.333 0 1 1-.47-.47l2.427-2.433L.767 1.57a.333.333 0 0 1 .471-.471l2.665 2.665Z"
        />
      </svg>
    );
  } else if (isLeft && isTiny) {
    return (
      <svg
        className={classes}
        width="5"
        height="8"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        role="none"
      >
        <path
          fillRule="evenodd"
          d="M1.097 3.763a.335.335 0 0 0 0 .473l2.665 2.666a.333.333 0 1 0 .47-.47L1.806 3.998l2.428-2.43a.333.333 0 0 0-.471-.471L1.097 3.763Z"
        />
      </svg>
    );
  } else if (isDown && isTiny) {
    return (
      <svg
        className={classes}
        width="8"
        height="5"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        role="none"
      >
        <path
          fillRule="evenodd"
          d="M3.764 4.236c.131.13.341.13.472 0l2.666-2.667a.333.333 0 1 0-.471-.471L4 3.528l-2.43-2.43a.333.333 0 1 0-.471.471l2.665 2.667Z"
        />
      </svg>
    );
  } else if (isUp && isTiny) {
    return (
      <svg
        className={classes}
        width="8"
        height="5"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        role="none"
      >
        <path
          fillRule="evenodd"
          d="M3.764 1.098a.334.334 0 0 1 .472 0l2.666 2.667a.333.333 0 1 1-.471.47L4 1.807l-2.43 2.43a.333.333 0 1 1-.471-.471l2.665-2.667Z"
        />
      </svg>
    );
  }
};

export default IconArrowHead;
